import type { TRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@servwell/api/router';

/**
 * The one way the app asks "who is signed in?" (MAP 225a). The gate and the invite page read
 * `me.session` through this and nothing else, so both get the same answer, and the same rules.
 */

/** How long the question may go unanswered before the gate says it couldn't reach us. */
export const SESSION_TIMEOUT_MS = 15_000;

export class SessionTimeoutError extends Error {
  constructor() {
    super('Servwell did not answer in time.');
    this.name = 'SessionTimeoutError';
  }
}

function withTimeout<T>(answer: T | Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new SessionTimeoutError()), ms);
    Promise.resolve(answer).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * `me.session`'s options, with a deadline. A device that reports itself online but gets
 * nothing through (one bar, a captive portal) used to hold the skeleton until the operating
 * system gave up; now it reaches the error screen and its "Try again" (C3). A request that
 * timed out is not retried: the screen offers that.
 *
 * `networkMode: 'online'` (MAP 225b): with no signal the question PAUSES instead of
 * making an `offlineFirst` attempt that fails into the error screen. Offline at boot, the
 * gate then draws the saved account; when the signal returns, TanStack continues the paused
 * request itself, and no error state exists to flash in between. A captive portal is
 * unchanged: online by `navigator`, so the deadline still leads to the error screen.
 */
export function sessionQueryOptions(trpc: TRPCOptionsProxy<AppRouter>) {
  const options = trpc.me.session.queryOptions();
  const fetchSession = options.queryFn!;
  return {
    ...options,
    networkMode: 'online' as const,
    queryFn: (context: Parameters<typeof fetchSession>[0]) =>
      withTimeout(fetchSession(context), SESSION_TIMEOUT_MS),
    retry: (failures: number, error: unknown) =>
      !(error instanceof SessionTimeoutError) && failures < 1,
  };
}
